import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, CardHeader, Spinner } from '../components/ui';
import {
  BaseProfileFields,
  EMPTY_BASE_PROFILE,
  validateBaseProfile,
  type BaseProfileErrors,
  type BaseProfileState,
} from '../components/onboarding/BaseProfileFields';
import { TermsAcceptanceStep } from '../components/onboarding/TermsAcceptanceStep';
import {
  completeCandidate,
  CURRENT_TERMS_VERSION,
  dashboardURLFor,
  deriveTerritory,
  extractApiErrorMessage,
  type BaseProfileBody,
} from '../lib/auth-onboarding';
import { buildOptionalConsents } from '../lib/consents';

export default function OnboardingCandidatePage(): JSX.Element {
  const navigate = useNavigate();
  // Token arrives in the URL fragment (#token=...) to avoid server logs. Capture
  // it once, then scrub the fragment so the bearer capability never lingers in
  // the address bar / history / referrer.
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '');

  const [step, setStep] = useState<'profile' | 'terms'>('profile');
  const [state, setState] = useState<BaseProfileState>(EMPTY_BASE_PROFILE);
  const [errors, setErrors] = useState<BaseProfileErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (window.location.hash) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);

  useEffect(() => {
    if (!token) {
      navigate('/signup', { replace: true });
    }
  }, [token, navigate]);

  useEffect(() => {
    let cancelled = false;
    deriveTerritory()
      .then((d) => {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          territory: d.territory,
          territorySource: d.territory_source,
        }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  function update<K extends keyof BaseProfileState>(key: K, next: BaseProfileState[K]): void {
    setState((prev) => ({ ...prev, [key]: next }));
  }



  function handleContinue(): void {
    const v = validateBaseProfile(state);
    setErrors(v);
    if (Object.keys(v).length > 0) return;
    setSubmitError(null);
    setStep('terms');
  }

  async function handleSubmit(): Promise<void> {
    const county = state.county.trim();
    const body: BaseProfileBody = {
      territory: state.territory,
      territory_source: state.territorySource,
      phone: state.phone,
      postcode: state.postcode,
      // Candidates never see a T&Cs tick-box — pressing "Create account"
      // is the acceptance (frame C-3), so the version is always sent.
      accept_terms_version: CURRENT_TERMS_VERSION,
      optional_consents: buildOptionalConsents({
        audience: 'candidate',
        marketingOptOut: state.marketingOptOut,
        // WhatsApp job-alert consent is implied by supplying the optional
        // mobile number on the details step (frame C-2).
        hasMobile: Boolean(state.phone.trim()),
      }),
      ...(county ? { county } : {}),
    };

    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await completeCandidate(body, token);
      window.location.replace(`${dashboardURLFor('candidate')}/auth/callback#token=${res.token}`);
    } catch (err) {
      setSubmitError(extractApiErrorMessage(err));
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <div className="mx-auto flex w-full max-w-md px-4 py-8 sm:px-6 sm:py-10 xl:py-12">
        <Card variant="elevated" className="w-full text-center">
          <div className="flex flex-col items-center gap-3 py-4">
            <Spinner label="Preparing candidate onboarding" />
            <p className="text-sm text-text-secondary">Preparing your candidate onboarding…</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 xl:py-12">
      {step === 'profile' ? (
        <Card>
          <CardHeader
            title={<h1 className="whitespace-normal">Complete your candidate profile</h1>}
            trailing={<span className="whitespace-nowrap text-sm text-text-secondary">Step 1 of 2</span>}
            className="flex-col md:flex-row"
          />
          <CardBody>
            <p className="mb-6 text-sm text-text-secondary">
              Just a couple of details so we can match you to the right roles.
            </p>
            <form
              className="flex flex-col gap-5"
              onSubmit={(e) => {
                e.preventDefault();
                handleContinue();
              }}
              noValidate
            >
              <BaseProfileFields
                value={state}
                errors={errors}
                audience="candidate"
                disabled={submitting}
                onChange={update}
              />
              <Button type="submit" className="w-full">
                Continue
              </Button>
            </form>
          </CardBody>
        </Card>
      ) : (
        <TermsAcceptanceStep
          audience="candidate"
          accepted={state.acceptedTerms}
          marketingOptOut={state.marketingOptOut}
          onToggleAccepted={(next) => update('acceptedTerms', next)}
          onToggleMarketingOptOut={(next) => update('marketingOptOut', next)}
          onBack={() => setStep('profile')}
          onSubmit={() => void handleSubmit()}
          submitting={submitting}
          submitError={submitError}
        />
      )}
    </div>
  );
}
